import { Link } from 'react-router-dom';
import logo from '@/assets/logo.svg';

const Privacy = () => (
	<div className="min-h-screen bg-grey-50">
		{/* Navbar */}
		<nav className="sticky top-0 z-10 flex items-center gap-6 bg-white px-8 py-4 shadow-[0_1px_0_0_var(--color-grey-100)]">
			<Link to="/" className="mr-auto shrink-0">
				<img src={`${logo}`} alt="Elevate logo" className="w-[120px]" />
			</Link>
			<Link
				to="/course-map"
				className="hidden text-sm font-poppinsSemiBold text-purple-900 no-underline transition-colors hover:text-purple-700 sm:block"
			>
				Course Map
			</Link>
			<Link
				to="/"
				className="hidden text-sm font-poppinsSemiBold text-purple-900 no-underline transition-colors hover:text-purple-700 sm:block"
			>
				Course Alignment
			</Link>
		</nav>

		{/* Page header */}
		<header className="border-b border-grey-100 bg-white px-8 py-12">
			<div className="mx-auto max-w-[1000px]">
				<p className="mb-2 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
					Legal
				</p>
				<h1 className="mb-3 text-4xl font-poppinsSemiBold text-grey-900">Privacy Policy</h1>
				<p className="text-sm text-grey-500">Last updated: April 9, 2026</p>
			</div>
		</header>

		{/* Content */}
		<main className="px-8 py-12">
			<div className="mx-auto max-w-[1000px] space-y-10">
				{/* Intro */}
				<section>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						The information on this site may not be reproduced, transmitted, or copied without the
						express written permission of Stakk IO.
					</p>
					<p className="text-sm leading-relaxed text-grey-600">
						This Privacy Policy describes Our policies and procedures on the collection, use and
						disclosure of Your information when You use the Service. Terms are subject to change.
					</p>
				</section>

				<hr className="border-grey-100" />

				{/* Interpretation & Definitions */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Overview
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Interpretation and Definitions
					</h3>
					<p className="mb-4 text-sm leading-relaxed text-grey-600">
						The words of which the initial letter is capitalized have meanings defined under the
						following conditions. The following definitions shall have the same meaning regardless
						of whether they appear in singular or in plural.
					</p>
					<div className="rounded-xl border border-grey-100 bg-white">
						<dl className="divide-y divide-grey-100">
							{[
								[
									'Account',
									'A unique account created for You to access our Service or parts of our Service.',
								],
								[
									'Company',
									'Referred to as "the Company", "We", "Us" or "Our" in this Agreement — refers to Stakk IO.',
								],
								[
									'Cookies',
									'Small files placed on Your computer, mobile device or any other device by a website, containing details of Your browsing history on that website among its many uses.',
								],
								['Country', 'Indiana, United States'],
								[
									'Device',
									'Any device that can access the Service such as a computer, a cellphone or a digital tablet.',
								],
								[
									'Personal Data',
									'Any information that relates to an identified or identifiable individual.',
								],
								['Service', 'The Website.'],
								[
									'Service Provider',
									'Any natural or legal person who processes data on behalf of the Company — third-party companies or individuals employed to facilitate or analyze the Service.',
								],
								[
									'Usage Data',
									'Data collected automatically, either generated by the use of the Service or from the Service infrastructure itself (for example, the duration of a page visit).',
								],
								['Website', `Elevate, accessible from ${window.location.origin}`],
								[
									'You',
									'The individual accessing or using the Service, or the company or legal entity on behalf of which such individual is accessing or using the Service.',
								],
							].map(([term, definition]) => (
								<div key={term} className="grid grid-cols-[180px_1fr] gap-4 px-5 py-4 text-sm">
									<dt className="font-poppinsSemiBold text-grey-800">{term}</dt>
									<dd className="leading-relaxed text-grey-600">{definition}</dd>
								</div>
							))}
						</dl>
					</div>
				</section>

				<hr className="border-grey-100" />

				{/* Data Collected */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Data Collection
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Types of Data Collected
					</h3>

					<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">Personal Data</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						While using Our Service, We may ask You to provide certain personally identifiable
						information. This may include, but is not limited to:
					</p>
					<ul className="mb-6 space-y-1 pl-5">
						{['Email address', 'First and last name', 'Usage Data'].map((item) => (
							<li key={item} className="flex items-center gap-2 text-sm text-grey-600">
								<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-purple-700" />
								{item}
							</li>
						))}
					</ul>

					<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">Usage Data</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						Usage Data is collected automatically when using the Service. It may include Your
						device's IP address, browser type and version, the pages You visit, the time and date of
						Your visit, time spent on those pages, unique device identifiers, and other diagnostic
						data.
					</p>
					<p className="mb-6 text-sm leading-relaxed text-grey-600">
						When You access the Service through a mobile device, We may also collect information
						such as the type of mobile device, mobile device unique ID, mobile operating system, and
						mobile Internet browser type.
					</p>

					<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">
						Tracking Technologies and Cookies
					</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						We use Cookies and similar tracking technologies to track activity on Our Service and
						store certain information. Technologies used include:
					</p>
					<div className="mb-4 space-y-3">
						<div className="rounded-lg border border-grey-100 bg-white p-4">
							<p className="mb-1 text-sm font-poppinsSemiBold text-grey-800">Browser Cookies</p>
							<p className="text-sm leading-relaxed text-grey-600">
								A small file placed on Your Device. You can instruct Your browser to refuse all
								Cookies or indicate when a Cookie is being sent. If You do not accept Cookies, some
								parts of our Service may not be available.
							</p>
						</div>
						<div className="rounded-lg border border-grey-100 bg-white p-4">
							<p className="mb-1 text-sm font-poppinsSemiBold text-grey-800">Web Beacons</p>
							<p className="text-sm leading-relaxed text-grey-600">
								Small electronic files (also known as clear gifs, pixel tags, or single-pixel gifs)
								used to count users who have visited certain pages or opened emails, and for related
								website statistics.
							</p>
						</div>
					</div>
					<p className="mb-4 text-sm leading-relaxed text-grey-600">
						Cookies can be "Persistent" (remaining on Your device when You go offline) or "Session"
						(deleted when You close Your browser). You can learn more at{' '}
						<a
							href="https://www.allaboutcookies.org"
							target="_blank"
							rel="noopener noreferrer"
							className="font-poppinsSemiBold text-purple-700 no-underline hover:text-purple-900"
						>
							allaboutcookies.org
						</a>
						.
					</p>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						We use both Session and Persistent Cookies for the purposes below:
					</p>
					<div className="rounded-xl border border-grey-100 bg-white">
						<dl className="divide-y divide-grey-100">
							{[
								{
									name: 'Necessary / Essential Cookies',
									type: 'Session',
									purpose:
										'Essential to provide You with services available through the Website and to authenticate users and prevent fraudulent use.',
								},
								{
									name: 'Cookie Policy Acceptance Cookies',
									type: 'Persistent',
									purpose: 'Identify if users have accepted the use of cookies on the Website.',
								},
								{
									name: 'Functionality Cookies',
									type: 'Persistent',
									purpose:
										'Allow us to remember choices You make when You use the Website, such as login details or language preference, for a more personal experience.',
								},
							].map(({ name, type, purpose }) => (
								<div key={name} className="px-5 py-4 text-sm">
									<div className="mb-1 flex items-center gap-3">
										<span className="font-poppinsSemiBold text-grey-800">{name}</span>
										<span className="rounded-full bg-purple-100 px-2 py-0.5 text-md font-poppinsSemiBold text-purple-700">
											{type}
										</span>
									</div>
									<p className="leading-relaxed text-grey-600">{purpose}</p>
								</div>
							))}
						</dl>
					</div>
				</section>

				<hr className="border-grey-100" />

				{/* Third-party services */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Data Processors
					</h2>
					<h3 className="mb-2 text-xl font-poppinsSemiBold text-grey-900">
						Third-Party Service Providers
					</h3>
					<p className="mb-4 text-sm leading-relaxed text-grey-600">
						We use the following third-party services to operate Elevate. Each acts as a data
						processor on Our behalf and is subject to their own privacy policy.
					</p>
					<div className="grid gap-3 sm:grid-cols-2">
						{[
							{
								name: 'Supabase',
								role: 'Authentication & Database',
								description:
									'Processes account credentials and application data as Our primary backend infrastructure.',
							},
							{
								name: 'Sentry',
								role: 'Error Tracking',
								description:
									'Captures diagnostic information, application state at the time of errors, and limited session recordings for debugging purposes.',
							},
							{
								name: 'EmailJS',
								role: 'Transactional Email',
								description:
									'Delivers account welcome emails and password reset links. Your email address is passed to EmailJS solely for this purpose.',
							},
							{
								name: 'Cloudinary',
								role: 'Image Storage',
								description: 'Stores and serves profile images and other uploaded media.',
							},
						].map(({ name, role, description }) => (
							<div key={name} className="rounded-xl border border-grey-100 bg-white p-5">
								<div className="mb-3 flex items-start justify-between gap-2">
									<p className="font-poppinsSemiBold text-grey-900">{name}</p>
									<span className="shrink-0 rounded-full bg-purple-100 px-2 py-0.5 text-md font-poppinsSemiBold text-purple-700">
										{role}
									</span>
								</div>
								<p className="text-sm leading-relaxed text-grey-600">{description}</p>
							</div>
						))}
					</div>
				</section>

				<hr className="border-grey-100" />

				{/* Use of Personal Data */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Data Use
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Use of Your Personal Data
					</h3>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						The Company may use Personal Data for the following purposes:
					</p>
					<ul className="mb-6 space-y-2">
						{[
							[
								'To provide and maintain our Service',
								'including to monitor the usage of our Service.',
							],
							[
								'To manage Your Account',
								'to manage Your registration as a user of the Service. The Personal Data You provide can give You access to different functionalities of the Service available to registered users.',
							],
							[
								'For the performance of a contract',
								'the development, compliance and undertaking of the purchase contract for the products, items or services You have purchased or of any other contract with Us through the Service.',
							],
							[
								'To contact You',
								'by email, telephone calls, SMS, or other equivalent forms of electronic communication regarding updates or informative communications related to the functionalities, products or contracted services, including security updates.',
							],
							[
								'To provide You',
								'with news, special offers and general information about goods, services and events similar to those you have already purchased or enquired about, unless You have opted out.',
							],
							['To manage Your requests', 'to attend and manage Your requests to Us.'],
							[
								'For business transfers',
								'We may use Your information to evaluate or conduct a merger, divestiture, restructuring, or other sale or transfer of some or all of Our assets, in which Personal Data may be among the assets transferred.',
							],
							[
								'For other purposes',
								'such as data analysis, identifying usage trends, determining the effectiveness of promotional campaigns, and evaluating and improving Our Service.',
							],
						].map(([bold, rest]) => (
							<li key={bold} className="flex gap-2 text-sm leading-relaxed text-grey-600">
								<span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-700" />
								<span>
									<strong className="font-poppinsSemiBold text-grey-800">{bold}:</strong> {rest}
								</span>
							</li>
						))}
					</ul>

					<h4 className="mb-3 text-sm font-poppinsSemiBold text-grey-800">
						Sharing Your Personal Information
					</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						We may share Your personal information in the following situations:
					</p>
					<ul className="space-y-2">
						{[
							[
								'With Service Providers',
								'We may share Your personal information with Service Providers to monitor and analyze the use of our Service and to contact You.',
							],
							[
								'For business transfers',
								'We may share or transfer Your personal information in connection with any merger, sale of Company assets, financing, or acquisition of all or a portion of Our business.',
							],
							[
								'With Affiliates',
								'We may share Your information with Our affiliates, including Our parent company and subsidiaries, requiring them to honor this Privacy Policy.',
							],
							[
								'With business partners',
								'We may share Your information with Our business partners to offer You certain products, services or promotions.',
							],
							[
								'With other users',
								'When You share personal information or otherwise interact in public areas of the Service, such information may be viewed by all users and may be publicly distributed.',
							],
							[
								'With Your consent',
								'We may disclose Your personal information for any other purpose with Your explicit consent.',
							],
						].map(([bold, rest]) => (
							<li key={bold} className="flex gap-2 text-sm leading-relaxed text-grey-600">
								<span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-purple-700" />
								<span>
									<strong className="font-poppinsSemiBold text-grey-800">{bold}:</strong> {rest}
								</span>
							</li>
						))}
					</ul>
				</section>

				<hr className="border-grey-100" />

				{/* Retention & Transfer */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Data Handling
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Retention and Transfer of Your Personal Data
					</h3>
					<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">Retention</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						The Company will retain Your Personal Data only for as long as is necessary for the
						purposes set out in this Privacy Policy, including to comply with our legal obligations,
						resolve disputes, and enforce our legal agreements and policies.
					</p>
					<p className="mb-6 text-sm leading-relaxed text-grey-600">
						Usage Data is generally retained for a shorter period, except when used to strengthen
						the security or improve the functionality of Our Service, or when We are legally
						obligated to retain it for longer.
					</p>
					<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">Transfer</h4>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						Your information, including Personal Data, is processed at the Company's operating
						offices and wherever the parties involved in the processing are located. This
						information may be transferred to — and maintained on — computers located outside of
						Your state, province, country or other governmental jurisdiction where data protection
						laws may differ from Your jurisdiction.
					</p>
					<p className="text-sm leading-relaxed text-grey-600">
						Your consent to this Privacy Policy followed by Your submission of such information
						represents Your agreement to that transfer. The Company will take all steps reasonably
						necessary to ensure Your data is treated securely and in accordance with this Privacy
						Policy.
					</p>
				</section>

				<hr className="border-grey-100" />

				{/* Disclosure */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Disclosure
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Disclosure of Your Personal Data
					</h3>
					<div className="space-y-4">
						<div>
							<h4 className="mb-1 text-sm font-poppinsSemiBold text-grey-800">
								Business Transactions
							</h4>
							<p className="text-sm leading-relaxed text-grey-600">
								If the Company is involved in a merger, acquisition, or asset sale, Your Personal
								Data may be transferred. We will provide notice before Your Personal Data is
								transferred and becomes subject to a different Privacy Policy.
							</p>
						</div>
						<div>
							<h4 className="mb-1 text-sm font-poppinsSemiBold text-grey-800">Law Enforcement</h4>
							<p className="text-sm leading-relaxed text-grey-600">
								Under certain circumstances, the Company may be required to disclose Your Personal
								Data if required to do so by law or in response to valid requests by public
								authorities (e.g. a court or a government agency).
							</p>
						</div>
						<div>
							<h4 className="mb-2 text-sm font-poppinsSemiBold text-grey-800">
								Other Legal Requirements
							</h4>
							<p className="mb-2 text-sm leading-relaxed text-grey-600">
								The Company may disclose Your Personal Data in the good faith belief that such
								action is necessary to:
							</p>
							<ul className="space-y-1">
								{[
									'Comply with a legal obligation',
									'Protect and defend the rights or property of the Company',
									'Prevent or investigate possible wrongdoing in connection with the Service',
									'Protect the personal safety of Users of the Service or the public',
									'Protect against legal liability',
								].map((item) => (
									<li key={item} className="flex items-center gap-2 text-sm text-grey-600">
										<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-purple-700" />
										{item}
									</li>
								))}
							</ul>
						</div>
					</div>
				</section>

				<hr className="border-grey-100" />

				{/* Security */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Security
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Security of Your Personal Data
					</h3>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						The security of Your Personal Data is important to Us. While We strive to use
						commercially acceptable means to protect Your Personal Data, no method of transmission
						over the Internet or method of electronic storage is 100% secure, and We cannot
						guarantee its absolute security.
					</p>
					<div className="rounded-xl border border-purple-100 bg-purple-50 p-5">
						<p className="text-sm leading-relaxed text-grey-700">
							<strong className="font-poppinsSemiBold text-grey-900">
								Data Breach Notification:
							</strong>{' '}
							In the event of a data breach that affects Your Personal Data, We will notify affected
							users within 72 hours of becoming aware of the incident, to the extent required by
							applicable law.
						</p>
					</div>
				</section>

				<hr className="border-grey-100" />

				{/* Children's Privacy */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Age Restrictions
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">Children's Privacy</h3>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						Our Service does not address anyone under the age of 13. We do not knowingly collect
						personally identifiable information from anyone under the age of 13. If You are a parent
						or guardian and You are aware that Your child has provided Us with Personal Data, please
						contact Us so We can take steps to remove that information from Our servers.
					</p>
					<p className="text-sm leading-relaxed text-grey-600">
						If We need to rely on consent as a legal basis for processing Your information and Your
						country requires consent from a parent, We may require Your parent's consent before We
						collect and use that information.
					</p>
				</section>

				<hr className="border-grey-100" />

				{/* Links */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						External Links
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Links to Other Websites
					</h3>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						Our Service may contain links to other websites that are not operated by Us. If You
						click on a third-party link, You will be directed to that third party's site. We
						strongly advise You to review the Privacy Policy of every site You visit.
					</p>
					<p className="text-sm leading-relaxed text-grey-600">
						We have no control over and assume no responsibility for the content, privacy policies,
						or practices of any third-party sites or services.
					</p>
				</section>

				<hr className="border-grey-100" />

				{/* Changes */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Updates
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">
						Changes to this Privacy Policy
					</h3>
					<p className="mb-3 text-sm leading-relaxed text-grey-600">
						We may update Our Privacy Policy from time to time. We will notify You of any changes by
						posting the new Privacy Policy on this page and updating the "Last updated" date at the
						top.
					</p>
					<p className="text-sm leading-relaxed text-grey-600">
						We will also notify You via email and/or a prominent notice on Our Service prior to the
						change becoming effective. You are advised to review this Privacy Policy periodically.
						Changes are effective when they are posted on this page.
					</p>
				</section>

				<hr className="border-grey-100" />

				{/* Contact */}
				<section>
					<h2 className="mb-1 text-md font-poppinsSemiBold uppercase tracking-widest text-purple-700">
						Contact
					</h2>
					<h3 className="mb-4 text-xl font-poppinsSemiBold text-grey-900">Contact Us</h3>
					<p className="mb-4 text-sm leading-relaxed text-grey-600">
						If you have any questions about this Privacy Policy, You can contact us:
					</p>
					<a
						href="mailto:elevate.support@stakk.io"
						className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-4 py-3 text-sm font-poppinsSemiBold text-purple-900 no-underline transition-colors hover:border-purple-300 hover:bg-purple-50"
					>
						elevate.support@stakk.io
					</a>
				</section>

				<div className="pb-4 text-center text-md text-grey-400">
					© {new Date().getFullYear()} Stakk IO. All rights reserved.
				</div>
			</div>
		</main>
	</div>
);

export default Privacy;
